import type { T_ChatV8UploadedAttachment } from "./transport";

export const CHAT_V8_ATTACHMENT_STAGING_LIMIT = 4;

export type T_ChatV8StagedAttachmentStatus =
  | "failed"
  | "uploaded"
  | "uploading";

export type T_ChatV8StagedAttachment = {
  /** Local identity chosen by the platform (not the server row cuid). */
  localId: string;
  mimeType: string;
  name: string;
  status: T_ChatV8StagedAttachmentStatus;
  /** Set once the upload grant round-trip completes. */
  uploaded: T_ChatV8UploadedAttachment | null;
};

export type T_ChatV8AttachmentStagingEvent =
  | { localId: string; mimeType: string; name: string; type: "STAGE" }
  | { localId: string; type: "REMOVE" }
  | { localId: string; type: "RETRY" }
  | { localId: string; type: "UPLOAD_FAILED" }
  | {
      localId: string;
      type: "UPLOAD_SUCCEEDED";
      uploaded: T_ChatV8UploadedAttachment;
    }
  | { type: "CLEAR" };

/**
 * Deterministic staged-attachment reducer shared by web and mobile.
 * Platforms own file selection and the upload network call; this owns which
 * attachments exist, their status, and what a send may include.
 */
export function reduceChatV8AttachmentStaging(
  staged: readonly T_ChatV8StagedAttachment[],
  event: T_ChatV8AttachmentStagingEvent,
): T_ChatV8StagedAttachment[] {
  switch (event.type) {
    case "STAGE": {
      if (staged.length >= CHAT_V8_ATTACHMENT_STAGING_LIMIT) {
        return [...staged];
      }
      if (staged.some((entry) => entry.localId === event.localId)) {
        return [...staged];
      }
      return [
        ...staged,
        {
          localId: event.localId,
          mimeType: event.mimeType,
          name: event.name,
          status: "uploading",
          uploaded: null,
        },
      ];
    }
    case "UPLOAD_SUCCEEDED":
      return staged.map((entry) =>
        entry.localId === event.localId
          ? { ...entry, status: "uploaded", uploaded: event.uploaded }
          : entry,
      );
    case "UPLOAD_FAILED":
      return staged.map((entry) =>
        entry.localId === event.localId
          ? { ...entry, status: "failed", uploaded: null }
          : entry,
      );
    case "RETRY":
      return staged.map((entry) =>
        entry.localId === event.localId && entry.status === "failed"
          ? { ...entry, status: "uploading" }
          : entry,
      );
    case "REMOVE":
      return staged.filter((entry) => entry.localId !== event.localId);
    case "CLEAR":
      return [];
  }
}

/** A send may proceed only when nothing is mid-flight or failed. */
export function canSendWithChatV8Attachments(
  staged: readonly T_ChatV8StagedAttachment[],
): boolean {
  return staged.every((entry) => entry.status === "uploaded");
}

/** Uploaded refs in staging order, for the sendMessage input. */
export function getChatV8SendAttachments(
  staged: readonly T_ChatV8StagedAttachment[],
): T_ChatV8UploadedAttachment[] {
  const uploads: T_ChatV8UploadedAttachment[] = [];
  for (const entry of staged) {
    if (entry.status === "uploaded" && entry.uploaded) {
      uploads.push(entry.uploaded);
    }
  }
  return uploads;
}
